import { useState } from 'react'

import { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '@/platform/family/api'
import { toAppError } from '@/shared/errors/envelope'
import { PinPad } from '@/shared/ui/PinPad'

/**
 * FR-01: the parent sets a PIN right after the family is created. The PIN
 * is entered twice; only the server ever hashes or stores it (SEC-C).
 */

export interface SetPinScreenProps {
  readonly onSetPin: (pin: string) => Promise<void>
}

export function SetPinScreen({ onSetPin }: SetPinScreenProps): React.JSX.Element {
  const [first, setFirst] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const confirm = async (pin: string) => {
    if (pin !== first) {
      setFirst(null)
      setError('Hai lần nhập không khớp. Hãy đặt lại PIN.')
      return
    }
    setBusy(true)
    try {
      await onSetPin(pin)
    } catch (e) {
      setFirst(null)
      setError(
        toAppError(e).code === 'INVALID_INPUT'
          ? `PIN gồm ${String(PIN_MIN_LENGTH)}–${String(PIN_MAX_LENGTH)} chữ số.`
          : 'Không lưu được PIN. Vui lòng thử lại.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-12">
      <div className="text-center">
        <p className="text-5xl" aria-hidden>
          🔒
        </p>
        <h1 className="mt-3 text-2xl font-semibold">Đặt PIN phụ huynh</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          PIN dùng để thoát chế độ học của bé. Đừng chia sẻ PIN với bé.
        </p>
      </div>
      {first === null ? (
        <PinPad
          key="first"
          title="Nhập PIN mới"
          hint={`${String(PIN_MIN_LENGTH)}–${String(PIN_MAX_LENGTH)} chữ số`}
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          submitLabel="Tiếp"
          onSubmit={(pin) => {
            setFirst(pin)
            setError(null)
          }}
        />
      ) : (
        <PinPad
          key="confirm"
          title="Nhập lại PIN"
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          busy={busy}
          submitLabel="Lưu"
          onSubmit={(pin) => void confirm(pin)}
          onCancel={() => {
            setFirst(null)
          }}
        />
      )}
    </main>
  )
}
